import { toPersianDigits } from "./numberFormat";

// Client-side checks that mirror the Invoice / ProformaInvoice models in
// documents/models.py, so problems show in Persian before the API is called.

const isBlank = (value) => String(value ?? "").trim() === "";
const tooLong = (label, max) => `${label}: حداکثر ${toPersianDigits(max)} نویسه مجاز است.`;
const NON_NEGATIVE = "باید عددی بزرگ‌تر یا مساوی صفر باشد.";

const itemLabel = (index) => `کالای ${toPersianDigits(index + 1)}`;

function checkFields(values, { labels, required, maxLengths, prefix = "" }) {
  const lines = [];
  required.forEach((name) => {
    if (isBlank(values[name])) lines.push(`${prefix}${labels[name]}: الزامی است.`);
  });
  Object.entries(maxLengths).forEach(([name, max]) => {
    if (String(values[name] ?? "").length > max) lines.push(prefix + tooLong(labels[name], max));
  });
  return lines;
}

function checkItemNumbers(item, labels, prefix) {
  const lines = [];
  if (!isBlank(item.commodity_code) && !/^\d+$/.test(item.commodity_code)) {
    lines.push(`${prefix}${labels.commodity_code}: فقط عدد مجاز است.`);
  }
  if (!(Number(item.quantity) > 0)) {
    lines.push(`${prefix}${labels.quantity}: باید بزرگ‌تر از صفر باشد.`);
  }
  if (isBlank(item.unit_price) || !(Number(item.unit_price) >= 0)) {
    lines.push(`${prefix}${labels.unit_price}: ${NON_NEGATIVE}`);
  }
  ["nw_kg", "gw_kg"].forEach((name) => {
    if (!isBlank(item[name]) && !(Number(item[name]) >= 0)) {
      lines.push(`${prefix}${labels[name]}: ${NON_NEGATIVE}`);
    }
  });
  if ("pack" in labels && !isBlank(item.pack) && !(Number.isInteger(Number(item.pack)) && Number(item.pack) >= 0)) {
    lines.push(`${prefix}${labels.pack}: باید عدد صحیح و بزرگ‌تر یا مساوی صفر باشد.`);
  }
  return lines;
}

function makeValidators({ fields, itemFields, freightField }) {
  const validateItem = (item, index = null) => {
    const prefix = index === null ? "" : `${itemLabel(index)} – `;
    return [
      ...checkFields(item, { ...itemFields, prefix }),
      ...checkItemNumbers(item, itemFields.labels, prefix),
    ];
  };

  const validate = (data) => {
    const lines = checkFields(data, fields);
    const freight = data[freightField];
    if (!isBlank(freight) && !(Number(freight) >= 0)) {
      lines.push(`${fields.labels[freightField]}: ${NON_NEGATIVE}`);
    }
    const items = data.items || [];
    if (!items.length) lines.push("حداقل یک کالا اضافه کنید.");
    items.forEach((item, index) => lines.push(...validateItem(item, index)));
    return lines;
  };

  return { validate, validateItem };
}

// ── Invoice ────────────────────────────────────────────────

export const FIELD_LABELS = {
  seller: "فروشنده",
  buyer: "خریدار",
  proforma: "ثبت سفارش",
  cottage: "اظهارنامه",
  invoice_id: "شناسه فاکتور",
  invoice_number: "شماره فاکتور",
  invoice_date: "تاریخ فاکتور",
  invoice_currency: "ارز",
  freight_charges: "هزینه حمل",
  country_of_origin: "کشور مبدأ",
  customer_tel: "شماره مشتری",
  port_of_loading: "بندر بارگیری",
  terms_of_delivery: "شرایط تحویل",
  means_of_transport: "وسیله حمل",
  relevant_location: "گمرک مقصد",
};

export const ITEM_LABELS = {
  title: "کالاها",
  label: itemLabel,
  fields: {
    description: "شرح کالا",
    quantity: "تعداد",
    unit_price: "قیمت واحد",
    commodity_code: "کد کالا (HS)",
    unit: "واحد",
    nw_kg: "وزن خالص",
    gw_kg: "وزن ناخالص",
    pack: "تعداد بسته‌بندی",
    origin: "مبدأ",
  },
};

export const API_ERROR_LABELS = { ...FIELD_LABELS, items: ITEM_LABELS };

const invoice = makeValidators({
  freightField: "freight_charges",
  fields: {
    labels: FIELD_LABELS,
    required: [
      "seller",
      "buyer",
      "invoice_number",
      "invoice_date",
      "invoice_currency",
      "terms_of_delivery",
      "means_of_transport",
      "relevant_location",
      "country_of_origin",
      "port_of_loading",
    ],
    maxLengths: {
      invoice_number: 50,
      customer_tel: 15,
      invoice_currency: 50,
      terms_of_delivery: 50,
      means_of_transport: 50,
      relevant_location: 555,
      country_of_origin: 555,
      port_of_loading: 555,
    },
  },
  itemFields: {
    labels: ITEM_LABELS.fields,
    required: ["description", "commodity_code", "unit", "origin"],
    maxLengths: { description: 1555, commodity_code: 9, unit: 55, origin: 55 },
  },
});

export const validateInvoice = invoice.validate;
/** Problems with one item; pass index to prefix them with "کالای n – ". */
export const validateInvoiceItem = invoice.validateItem;

// ── Proforma invoice ───────────────────────────────────────

export const PROFORMA_FIELD_LABELS = {
  seller: "فروشنده",
  buyer: "خریدار",
  proforma: "ثبت سفارش",
  proforma_invoice_id: "شناسه پروفورما",
  proforma_invoice_number: "شماره پروفورما",
  proforma_invoice_date: "تاریخ پروفورما",
  proforma_invoice_exp_date: "تاریخ اعتبار",
  proforma_invoice_currency: "ارز",
  proforma_freight_charges: "هزینه حمل",
  terms_of_delivery: "شرایط تحویل",
  terms_of_payment: "شرایط پرداخت",
  standard: "استاندارد",
  partial_shipment: "حمل به دفعات",
  means_of_transport: "وسیله حمل",
  country_of_origin: "کشور مبدأ",
  port_of_loading: "بندر بارگیری",
  relevant_location: "گمرک مقصد",
};

const PROFORMA_ITEM_FIELDS = {
  description: "شرح کالا",
  original_description: "شرح کالا",
  translated_description: "شرح انگلیسی کالا",
  quantity: "تعداد",
  unit_price: "قیمت واحد",
  commodity_code: "کد کالا (HS)",
  unit: "واحد",
  nw_kg: "وزن خالص",
  gw_kg: "وزن ناخالص",
  origin: "مبدأ",
};

export const PROFORMA_API_ERROR_LABELS = {
  ...PROFORMA_FIELD_LABELS,
  items: { title: "کالاها", label: itemLabel, fields: PROFORMA_ITEM_FIELDS },
};

const PROFORMA_REQUIRED = [
  "seller",
  "buyer",
  "proforma_invoice_number",
  "proforma_invoice_date",
  "proforma_invoice_exp_date",
  "proforma_invoice_currency",
  "terms_of_delivery",
  "terms_of_payment",
  "standard",
  "means_of_transport",
  "country_of_origin",
  "port_of_loading",
  "relevant_location",
];
const PROFORMA_MAX_LENGTHS = {
  proforma_invoice_number: 50,
  proforma_invoice_currency: 50,
  terms_of_delivery: 50,
  terms_of_payment: 50,
  standard: 50,
  means_of_transport: 50,
  relevant_location: 555,
  country_of_origin: 555,
  port_of_loading: 555,
};

/**
 * descriptionField is "description" in the create form's state and
 * "original_description" on records loaded from the API.
 */
function proformaValidators(descriptionField) {
  return makeValidators({
    freightField: "proforma_freight_charges",
    fields: { labels: PROFORMA_FIELD_LABELS, required: PROFORMA_REQUIRED, maxLengths: PROFORMA_MAX_LENGTHS },
    itemFields: {
      labels: PROFORMA_ITEM_FIELDS,
      required: [descriptionField, "commodity_code", "unit", "origin"],
      maxLengths: { [descriptionField]: 1555, commodity_code: 9, unit: 55, origin: 55 },
    },
  });
}

const proformaForm = proformaValidators("description");
const proformaRecord = proformaValidators("original_description");

export const validateProformaInvoiceForm = proformaForm.validate;
export const validateProformaInvoice = proformaRecord.validate;
export const validateProformaInvoiceItem = proformaRecord.validateItem;
